/* ============================================================
   Hero Carousel — section-hero-carousel.js
   Vanilla JS, no dependencies.
   Drop in /assets/ alongside the CSS file.
   ============================================================ */
(function () {
  'use strict';

  class HeroCarousel {
    constructor(root) {
      this.root  = root;
      this.track = root.querySelector('[data-carousel-track]');
      this.slides = Array.from(root.querySelectorAll('[data-carousel-slide]'));
      this.prevBtn = root.querySelector('[data-carousel-prev]');
      this.nextBtn = root.querySelector('[data-carousel-next]');
      this.dots = Array.from(root.querySelectorAll('[data-carousel-dot]'));
      this.live = root.querySelector('[data-carousel-live]');

      // Bail early if only one slide (no carousel behavior needed)
      if (this.slides.length < 2) {
        this.singleSlide = true;
      }

      // Settings from data attributes
      this.cfg = {
        autoplay:        root.dataset.autoplay === 'true',
        autoplaySpeed:   parseInt(root.dataset.autoplaySpeed, 10) || 6000,
        pauseOnHover:    root.dataset.pauseOnHover === 'true',
        loop:            root.dataset.loop === 'true',
        transition:      root.dataset.transition || 'slide',
        transitionSpeed: parseInt(root.dataset.transitionSpeed, 10) || 700,
        enableSwipe:     root.dataset.enableSwipe === 'true',
      };

      // State
      this.currentIndex   = 0;
      this.isAnimating    = false;
      this.autoplayTimer  = null;
      this.isVisible      = true;
      this.isHovering     = false;
      this.prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.init();
    }

    init() {
      this.applyInitialState();
      if (this.singleSlide) return;

      this.bindNavigation();
      this.bindKeyboard();
      this.bindHover();
      this.bindSwipe();
      this.bindVisibility();
      this.bindTabVisibility();
      this.bindReducedMotion();

      this.maybeStartAutoplay();
    }

    /* ---------- Initial render ---------- */
    applyInitialState() {
      if (this.cfg.transition === 'fade') {
        this.slides.forEach((s, i) => s.classList.toggle('is-active', i === 0));
      } else {
        this.track.style.transform = 'translateX(0%)';
      }
    }

    /* ---------- Movement ---------- */
    goTo(index, opts = {}) {
      if (this.isAnimating) return;
      if (index === this.currentIndex && !opts.force) return;

      const last = this.slides.length - 1;

      // Loop or clamp
      if (index < 0) {
        if (!this.cfg.loop) return;
        index = last;
      } else if (index > last) {
        if (!this.cfg.loop) return;
        index = 0;
      }

      this.isAnimating = true;

      if (this.cfg.transition === 'fade') {
        this.slides[this.currentIndex].classList.remove('is-active');
        this.slides[index].classList.add('is-active');
      } else {
        this.track.style.transform = 'translateX(-' + (index * 100) + '%)';
      }

      this.currentIndex = index;
      this.updateDots();
      this.announce();

      window.setTimeout(() => {
        this.isAnimating = false;
      }, this.cfg.transitionSpeed);
    }

    next() { this.goTo(this.currentIndex + 1); }
    prev() { this.goTo(this.currentIndex - 1); }

    /* ---------- UI updates ---------- */
    updateDots() {
      this.dots.forEach((dot, i) => {
        const active = i === this.currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    announce() {
      if (!this.live) return;
      this.live.textContent = 'Slide ' + (this.currentIndex + 1) + ' of ' + this.slides.length;
    }

    /* ---------- Autoplay ---------- */
    maybeStartAutoplay() {
      if (!this.cfg.autoplay || this.prefersReduced) return;
      if (!this.isVisible || this.isHovering) return;
      this.startAutoplay();
    }

    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimer = window.setInterval(() => this.next(), this.cfg.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    resetAutoplay() {
      if (!this.cfg.autoplay || this.prefersReduced) return;
      this.stopAutoplay();
      this.maybeStartAutoplay();
    }

    /* ---------- Bindings ---------- */
    bindNavigation() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => { this.prev(); this.resetAutoplay(); });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => { this.next(); this.resetAutoplay(); });
      }
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => { this.goTo(i); this.resetAutoplay(); });
      });
    }

    bindKeyboard() {
      this.root.setAttribute('tabindex', '-1');
      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this.prev(); this.resetAutoplay(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); this.resetAutoplay(); }
        if (e.key === 'Home')       { e.preventDefault(); this.goTo(0); this.resetAutoplay(); }
        if (e.key === 'End')        { e.preventDefault(); this.goTo(this.slides.length - 1); this.resetAutoplay(); }
      });
    }

    bindHover() {
      if (!this.cfg.pauseOnHover || !this.cfg.autoplay) return;
      this.root.addEventListener('mouseenter', () => {
        this.isHovering = true;
        this.stopAutoplay();
      });
      this.root.addEventListener('mouseleave', () => {
        this.isHovering = false;
        this.maybeStartAutoplay();
      });
      // Also pause when any control receives keyboard focus
      this.root.addEventListener('focusin', () => this.stopAutoplay());
      this.root.addEventListener('focusout', () => this.maybeStartAutoplay());
    }

    bindSwipe() {
      if (!this.cfg.enableSwipe) return;

      let startX = 0, startY = 0, deltaX = 0, deltaY = 0, isSwiping = false;
      const threshold = 50;

      this.root.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isSwiping = true;
      }, { passive: true });

      this.root.addEventListener('touchmove', (e) => {
        if (!isSwiping) return;
        deltaX = e.touches[0].clientX - startX;
        deltaY = e.touches[0].clientY - startY;
      }, { passive: true });

      this.root.addEventListener('touchend', () => {
        if (!isSwiping) return;
        isSwiping = false;
        // Only act on horizontal swipes
        if (Math.abs(deltaX) > threshold && Math.abs(deltaX) > Math.abs(deltaY)) {
          if (deltaX < 0) this.next();
          else this.prev();
          this.resetAutoplay();
        }
        deltaX = 0; deltaY = 0;
      }, { passive: true });
    }

    bindVisibility() {
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) this.maybeStartAutoplay();
          else this.stopAutoplay();
        });
      }, { threshold: 0.25 });
      io.observe(this.root);
      this._io = io;
    }

    bindTabVisibility() {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.maybeStartAutoplay();
      });
    }

    bindReducedMotion() {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      const handler = (e) => {
        this.prefersReduced = e.matches;
        if (e.matches) this.stopAutoplay();
        else this.maybeStartAutoplay();
      };
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else if (mq.addListener) mq.addListener(handler); // older Safari
    }

    /* ---------- Cleanup ---------- */
    destroy() {
      this.stopAutoplay();
      if (this._io) this._io.disconnect();
    }
  }

  /* ============================================================
     Initialization
     ============================================================ */
  const SELECTOR = '[data-hero-carousel]';

  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll(SELECTOR).forEach((el) => {
      if (!el.__heroCarousel) {
        el.__heroCarousel = new HeroCarousel(el);
      }
    });
  }

  function destroy(el) {
    if (el && el.__heroCarousel) {
      el.__heroCarousel.destroy();
      el.__heroCarousel = null;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  /* ============================================================
     Shopify Theme Editor integration
     ============================================================ */
  document.addEventListener('shopify:section:load', (e) => {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target.querySelector(SELECTOR);
    if (el) destroy(el);
  });

  document.addEventListener('shopify:section:select', (e) => {
    const el = e.target.querySelector(SELECTOR);
    if (el && el.__heroCarousel) el.__heroCarousel.stopAutoplay();
  });

  document.addEventListener('shopify:section:deselect', (e) => {
    const el = e.target.querySelector(SELECTOR);
    if (el && el.__heroCarousel) el.__heroCarousel.maybeStartAutoplay();
  });

  // Jump to the slide an admin is editing
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target.closest(SELECTOR);
    const slide = e.target.closest('[data-carousel-slide]');
    if (el && slide && el.__heroCarousel) {
      const i = parseInt(slide.dataset.index, 10);
      el.__heroCarousel.stopAutoplay();
      el.__heroCarousel.goTo(i, { force: true });
    }
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    const el = e.target.closest(SELECTOR);
    if (el && el.__heroCarousel) el.__heroCarousel.maybeStartAutoplay();
  });
})();
